// 3-step Account Deletion modal — App-Store-mandatory flow.
// Step 1: Warning + bullet list of what's deleted vs retained.
// Step 2: Type "DELETE" + tick the consent checkbox.
// Step 3: Final confirm button — fires backend DELETE /api/users/me/delete.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { axiosInstance } from '../../config/axios';
import { useAuth } from '../../context/AuthContext';

export default function DeleteAccountModal({ onClose }) {
  const [step, setStep] = useState(1);
  const [confirmText, setConfirmText] = useState('');
  const [agreed, setAgreed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { logout } = useAuth();

  const canProceedStep2 = confirmText.trim().toUpperCase() === 'DELETE' && agreed;

  const handleConfirmDelete = async () => {
    setSubmitting(true);
    try {
      await axiosInstance.delete('/users/me/delete');
      // Best-effort logout — the backend has already wiped Firebase auth.
      try { await logout(); } catch { /* ignore */ }
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      toast.success('Your account has been deleted.');
      // Brief pause so user sees the success toast before the hard redirect.
      setTimeout(() => {
        window.location.href = '/auth';
      }, 800);
    } catch (err) {
      console.error('Delete account error:', err);
      toast.error(err?.response?.data?.detail || 'Failed to delete account. Please try again or contact support.');
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[1100] p-4"
      onClick={onClose}
      data-testid="delete-account-modal-backdrop"
    >
      <div
        className="bg-white rounded-3xl p-6 max-w-md w-full max-h-[85vh] overflow-y-auto relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="delete-account-modal"
        style={{ fontFamily: "'Outfit', sans-serif" }}
      >
        <button
          onClick={onClose}
          className="absolute top-3 right-3 p-2 rounded-full hover:bg-gray-100"
          aria-label="Close"
          data-testid="delete-account-close-btn"
        >
          <i className="ph-bold ph-x text-xl text-gray-500" />
        </button>

        <div className="flex items-center gap-2 mb-2">
          <i className="ph-fill ph-warning-circle text-2xl text-[#E54562]" />
          <h2 className="text-xl font-extrabold text-[#2B3A67]">Delete Account</h2>
        </div>

        {step === 1 && (
          <div data-testid="delete-account-step-1">
            <p className="text-sm text-gray-600 mb-4">
              This action is <strong>permanent</strong>. Once deleted, you cannot recover your account or its data.
            </p>

            <div className="bg-rose-50 border border-rose-100 rounded-2xl p-4 mb-3">
              <p className="text-xs font-bold uppercase tracking-wider text-rose-700 mb-2">What gets deleted</p>
              <ul className="text-xs text-gray-700 space-y-1.5 list-disc pl-5">
                <li>Your name, email, mobile number, profile photo</li>
                <li>Notification preferences and push devices</li>
                <li>Your saved profile, bio, and preferences</li>
                <li>You'll be signed out of every device</li>
              </ul>
            </div>

            <div className="bg-amber-50 border border-amber-100 rounded-2xl p-4 mb-4">
              <p className="text-xs font-bold uppercase tracking-wider text-amber-700 mb-2">What we're required to retain</p>
              <ul className="text-xs text-gray-700 space-y-1.5 list-disc pl-5">
                <li>Booking & invoice records (8 yrs — Indian tax law)</li>
                <li>Tournament/match results — your name is anonymised</li>
                <li>Security audit logs (12 months)</li>
              </ul>
              <p className="text-[10px] text-gray-500 mt-2">
                See our{' '}
                <Link to="/legal/account-deletion" className="underline" target="_blank" rel="noreferrer">
                  Account Deletion policy
                </Link>{' '}
                for full details.
              </p>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 font-bold text-sm"
                data-testid="delete-account-cancel-1"
              >
                Cancel
              </button>
              <button
                onClick={() => setStep(2)}
                className="flex-1 py-3 rounded-2xl bg-gray-900 text-white font-bold text-sm"
                data-testid="delete-account-continue-1"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 2 && (
          <div data-testid="delete-account-step-2">
            <p className="text-sm text-gray-600 mb-4">
              To confirm, type <strong className="text-[#E54562]">DELETE</strong> below and tick the consent box.
            </p>

            <input
              type="text"
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="Type DELETE to confirm"
              className="w-full border-2 border-gray-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-[#E54562] mb-4"
              data-testid="delete-account-confirm-input"
              autoFocus
            />

            <label className="flex items-start gap-2 mb-5 cursor-pointer">
              <input
                type="checkbox"
                checked={agreed}
                onChange={(e) => setAgreed(e.target.checked)}
                className="mt-1"
                data-testid="delete-account-agree-checkbox"
              />
              <span className="text-xs text-gray-600">
                I understand this is permanent and acknowledge that bookings, invoices, and anonymised match records may be retained as required by law.
              </span>
            </label>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(1)}
                className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 font-bold text-sm"
                data-testid="delete-account-back-2"
              >
                Back
              </button>
              <button
                onClick={() => setStep(3)}
                disabled={!canProceedStep2}
                className="flex-1 py-3 rounded-2xl bg-[#E54562] text-white font-bold text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="delete-account-continue-2"
              >
                Continue
              </button>
            </div>
          </div>
        )}

        {step === 3 && (
          <div data-testid="delete-account-step-3">
            <p className="text-sm text-gray-600 mb-2">
              Last step. Pressing the button below will delete your account immediately.
            </p>
            <p className="text-xs text-gray-500 mb-5">
              You'll be signed out and redirected to the login page.
            </p>

            <div className="flex gap-3">
              <button
                onClick={() => setStep(2)}
                disabled={submitting}
                className="flex-1 py-3 rounded-2xl border-2 border-gray-200 text-gray-600 font-bold text-sm disabled:opacity-50"
                data-testid="delete-account-back-3"
              >
                Back
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={submitting}
                className="flex-1 py-3 rounded-2xl bg-[#E54562] text-white font-extrabold text-sm disabled:opacity-60"
                data-testid="delete-account-final-confirm"
              >
                {submitting ? 'Deleting…' : 'Delete my account'}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
